/* lokator100 facets v1.1.2 — desktop filter UI in a modal mobile drawer.
 * One shared form, native inline details/accordions at every viewport width.
 * No dependencies. The grid is not rewritten. Mobile changes commit on Apply.
 */
(() => {
  'use strict';

  function buildFilterURL(currentHref, actionHref, entries, managedNames, pageParam = 'page') {
    const current = new URL(currentHref);
    const url = new URL(actionHref, current);
    if (url.origin !== current.origin) throw new Error('Filter action must be same-origin.');
    url.search = current.search;
    url.hash = '';
    const names = new Set(managedNames);
    for (const [name] of entries) names.add(name);
    for (const name of names) url.searchParams.delete(name);
    for (const key of ['page', pageParam, 'section_id', 'sections']) url.searchParams.delete(key);
    for (const [name, value] of entries) {
      if (typeof value !== 'string') continue;
      if (name.startsWith('filter.') && value.trim() === '') continue;
      url.searchParams.append(name, value);
    }
    return url;
  }

  function clearFilterURL(currentHref, pageParam = 'page') {
    const url = new URL(currentHref);
    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith('filter.')) url.searchParams.delete(key);
    }
    for (const key of ['page', pageParam, 'section_id', 'sections']) url.searchParams.delete(key);
    url.hash = '';
    return url;
  }

  // Empty is an unset bound, not zero. Accept a decimal point or comma, no grouping.
  function parsePrice(raw) {
    const text = String(raw).trim();
    if (text === '') return null;
    if (!/^(?:\d+(?:[.,]\d{0,2})?|[.,]\d{1,2})$/.test(text)) return NaN;
    const value = Number(text.replace(',', '.'));
    return Number.isFinite(value) ? Math.round(value * 100) : NaN;
  }
  function hasFilterEntries(entries) {
    return [...entries].some(([name, value]) =>
      name.startsWith('filter.') && typeof value === 'string' && value.trim() !== ''
    );
  }

  const toAmount = cents => (cents / 100).toFixed(2);
  const clamp = (value, low, high) => Math.min(high, Math.max(low, value));

  // Snap within the other thumb's bound; keep the exact end of a fractional range reachable.
  function sliderValue(raw, step, maximum, lower, upper) {
    if (raw <= 0) return lower;
    if (raw >= maximum) return upper;
    return clamp(Math.round(raw / step) * step, lower, upper);
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { buildFilterURL, clearFilterURL, parsePrice, sliderValue, hasFilterEntries };
  }
  if (typeof window === 'undefined' || !window.customElements) return;
  if (window.customElements.get('lokator-facets')) return;

  window.__lokatorFacetsVersion = '1.1.2';

  class LokatorFacets extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.form = this.querySelector('[data-lkf-form]');
      if (!this.form) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.panel = this.querySelector('[data-lkf-panel]');
      this.content = this.querySelector('[data-lkf-content]');
      this.desktopHost = this.querySelector('[data-lkf-desktop-host]');
      this.drawerHost = this.querySelector('[data-lkf-drawer-host]');
      this.dialog = this.querySelector('[data-lkf-dialog]');
      this.toggle = this.querySelector('[data-lkf-toggle]');
      this.mobileBar = this.querySelector('[data-lkf-mobile-bar]');
      this.mobile = window.matchMedia('(max-width: 989px)');
      this.supportsDialog = typeof this.dialog?.showModal === 'function';
      this.groups = [...this.querySelectorAll('[data-lkf-group]')];
      this.prices = [...this.querySelectorAll('[data-lkf-price]')];
      this.pageParam = this.dataset.pageParam || 'page';
      this.increment = Math.max(1, Math.round(Number(this.dataset.priceIncrement || '1') * 100));
      if (!Number.isFinite(this.increment)) this.increment = 100;
      this.stateKey = `lokator-facets:v1:${window.location.pathname}:${this.id}`;
      this.inMobile = false;
      this.isOpen = false;
      this.pendingClear = false;
      this.dataset.lkfVersion = '1.1.2';
      this.initializeUnions();
      this.restoreGroupState();
      this.classList.add('lkf--enhanced');
      this.querySelectorAll('.lkf__value-search, [data-lkf-slider]').forEach(el => { el.hidden = false; });
      // Open an inline accordion before focusing its invalid price field.
      this.form.noValidate = true;
      try {
        this.moneyFormatter = new Intl.NumberFormat(this.dataset.locale || 'en', {
          style: 'currency', currency: this.dataset.currency || 'EUR'
        });
      } catch (_) { this.moneyFormatter = null; }
      this.syncLayout();
      this.updatePriceValidity();
      this.prices.forEach(group => this.syncPriceFromFields(group));
      this.updateGroupCounts();

      this.mobile.addEventListener('change', () => this.syncLayout(), options);
      this.toggle?.addEventListener('click', () => this.openDrawer(), options);
      this.querySelector('[data-lkf-close]')?.addEventListener('click', () => this.closeDrawer(), options);
      this.dialog?.addEventListener('keydown', event => this.trapTab(event), options);
      this.dialog?.addEventListener('cancel', event => {
        event.preventDefault();
        this.closeDrawer();
      }, options);
      this.dialog?.addEventListener('close', () => {
        // Handles an external close() as well as the component's own close button.
        if (this.isOpen) this.closeDrawer();
      }, options);
      this.dialog?.addEventListener('pointerdown', event => {
        this.backdropPointer = event.target === this.dialog && this.outsideDialog(event);
      }, options);
      this.dialog?.addEventListener('click', event => {
        if (this.backdropPointer && event.target === this.dialog && this.outsideDialog(event)) this.closeDrawer();
        this.backdropPointer = false;
      }, options);

      // Do not intercept summary clicks on mobile: the same native details
      // elements expand inline on both layouts. Multiple groups can stay open.
      this.groups.forEach(group => {
        group.addEventListener('toggle', () => {
          if (group.classList.contains('lkf__shortcut-group') && !group.open) group.open = true;
          this.saveState();
        }, options);
      });
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        this.submitFilters();
      }, options);
      this.form.addEventListener('change', event => {
        if (event.target.matches('[data-lkf-range]')) return;
        if (event.target.matches('[data-lkf-union-choice]')) {
          this.applyUnionSelection(event.target);
          this.updateGroupCounts();
          if (!this.inMobile && this.dataset.autoApply === 'true') this.form.requestSubmit();
          return;
        }
        if (event.target.matches('[data-lkf-bound]')) {
          this.updatePriceValidity();
          this.syncPriceFromFields(event.target.closest('[data-lkf-price]'));
          this.updateGroupCounts();
          return;
        }
        this.updateGroupCounts();
        if (!this.inMobile && this.dataset.autoApply === 'true' && event.target.matches('[data-lkf-choice], [data-lkf-sort]')) {
          this.form.requestSubmit();
        }
      }, options);
      this.form.addEventListener('input', event => {
        if (event.target.matches('[data-lkf-bound]')) {
          this.updatePriceValidity();
          this.syncPriceFromFields(event.target.closest('[data-lkf-price]'));
          this.updateGroupCounts();
        }
        if (event.target.matches('[data-lkf-range]')) this.rangeInput(event.target);
        if (event.target.matches('[data-lkf-value-search]')) this.searchValues(event.target);
      }, options);
      this.form.addEventListener('keydown', event => {
        if (event.key === 'Enter' && event.target.matches('[data-lkf-value-search]')) event.preventDefault();
        if (event.target.matches('[data-lkf-range]')) this.rangeKeydown(event);
      }, options);
      this.prices.forEach(group => this.bindSliderTrack(group, options));
      this.addEventListener('click', event => this.onLinkClick(event), options);
      window.addEventListener('pagehide', () => {
        this.saveState();
        if (this.isOpen) this.closeDrawer({ restoreFocus: false });
      }, options);
    }

    disconnectedCallback() {
      if (this.isOpen) this.closeDrawer({ restoreFocus: false });
      this.unlockScroll();
      this.controller?.abort();
      this.controller = null;
    }

    syncLayout() {
      const mobile = this.mobile.matches && this.supportsDialog;
      if (mobile && !this.inMobile) {
        this.inMobile = true;
        // Move the existing form; do not collapse groups or clone controls.
        this.drawerHost.append(this.panel);
      } else if (!mobile && this.inMobile) {
        if (this.isOpen) this.closeDrawer({ restoreFocus: false });
        this.inMobile = false;
        this.desktopHost.append(this.panel);
      }
      if (this.mobileBar) this.mobileBar.hidden = !mobile;
      this.classList.toggle('lkf--drawer-ready', mobile);
      this.toggle?.setAttribute('aria-expanded', String(this.isOpen));
    }

    openDrawer() {
      if (!this.inMobile || this.isOpen) return;
      this.snapshot = [...this.form.querySelectorAll('input, select')].map(input => ({
        input, value: input.value, checked: input.checked, disabled: input.disabled
      }));
      this.pendingClear = false;
      this.content.scrollTop = 0;
      this.dialog.showModal();
      this.isOpen = true;
      this.toggle.setAttribute('aria-expanded', 'true');
      this.updateClearVisibility();
      this.lockScroll();
      this.querySelector('[data-lkf-close]')?.focus({ preventScroll: true });
    }

    closeDrawer({ discard = true, restoreFocus = true } = {}) {
      if (discard && this.snapshot) {
        for (const state of this.snapshot) {
          state.input.value = state.value;
          state.input.checked = state.checked;
          state.input.disabled = state.disabled;
        }
        this.querySelectorAll('[data-lkf-value-search]').forEach(input => this.searchValues(input));
        this.updatePriceValidity();
        this.prices.forEach(group => this.syncPriceFromFields(group));
        this.updateGroupCounts();
      }
      this.isOpen = false;
      if (this.dialog?.open) this.dialog.close();
      this.unlockScroll();
      this.snapshot = null;
      this.pendingClear = false;
      this.updateGroupCounts();
      this.toggle?.setAttribute('aria-expanded', 'false');
      if (restoreFocus && this.inMobile && this.toggle?.isConnected) this.toggle.focus({ preventScroll: true });
    }

    trapTab(event) {
      if (event.key !== 'Tab' || !this.isOpen) return;
      const selector = 'a[href],button:not(:disabled),input:not(:disabled):not([type=hidden]),select:not(:disabled),textarea:not(:disabled),summary,[tabindex]';
      const items = [...this.dialog.querySelectorAll(selector)].filter(el => {
        if (el.tabIndex < 0 || el.closest('[inert]')) return false;
        return el.checkVisibility ? el.checkVisibility({ checkVisibilityCSS: true }) : el.getClientRects().length > 0;
      });
      const first = items[0], last = items.at(-1);
      if (!first) { event.preventDefault(); return; }
      const current = document.activeElement;
      if (event.shiftKey && (current === first || !items.includes(current))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (current === last || !items.includes(current))) {
        event.preventDefault(); first.focus();
      }
    }

    outsideDialog(event) {
      const rect = this.dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    }

    lockScroll() {
      if (this.scrollState) return;
      const body = document.body;
      this.scrollState = {
        x: window.scrollX, y: window.scrollY,
        position: body.style.position, top: body.style.top, left: body.style.left,
        width: body.style.width, overflow: body.style.overflow,
        htmlOverflow: document.documentElement.style.overflow
      };
      body.style.position = 'fixed';
      body.style.top = `-${this.scrollState.y}px`;
      body.style.left = `-${this.scrollState.x}px`;
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    }
    unlockScroll() {
      if (!this.scrollState) return;
      const state = this.scrollState;
      for (const key of ['position', 'top', 'left', 'width', 'overflow']) document.body.style[key] = state[key];
      document.documentElement.style.overflow = state.htmlOverflow;
      this.scrollState = null;
      window.scrollTo(state.x, state.y);
    }

    // The union checkbox has NO name: only the real native inputs are submitted.
    initializeUnions() {
      this.unionDisabled = new WeakMap();
      this.unions = [];
      this.querySelectorAll('[data-lkf-union]').forEach(wrapper => {
        try {
          const name = wrapper.dataset.lkfUnionParam;
          const values = JSON.parse(wrapper.dataset.lkfUnionValues || '[]');
          const toggle = wrapper.querySelector('[data-lkf-union-choice]');
          const operator = (wrapper.dataset.lkfUnionOperator || '').toUpperCase();
          if (!name?.startsWith('filter.') || operator !== 'OR' || !toggle ||
              !Array.isArray(values) || !values.length ||
              values.some(value => typeof value !== 'string' || !value)) return;
          const members = new Set(values);
          const controls = [...this.form.querySelectorAll('input')].filter(input =>
            input.name === name && (input.matches('[data-lkf-choice]') || input.type === 'hidden')
          );
          // Fail closed if another theme customization removed one of the inputs.
          if (values.some(value => !controls.some(input => input.value === value))) return;
          controls.forEach(input => this.unionDisabled.set(input, input.disabled));
          this.unions.push({ wrapper, toggle, name, members, controls });
          wrapper.hidden = false;
        } catch (error) {
          console.warn('[lokator filters] Condition shortcut unavailable; native Condition retained.', error);
        }
      });
    }

    updateUnionControls() {
      for (const union of this.unions || []) {
        const selected = new Set([...new FormData(this.form).entries()]
          .filter(([name, value]) => name === union.name && typeof value === 'string' && value !== '')
          .map(([, value]) => value));
        const selectedMembers = [...union.members].filter(value => selected.has(value)).length;
        const hasOtherValues = [...selected].some(value => !union.members.has(value));
        const complete = selectedMembers === union.members.size && !hasOtherValues;
        union.toggle.checked = complete;
        union.toggle.indeterminate = selectedMembers > 0 && !complete;
        union.toggle.disabled = selectedMembers === 0 && !union.controls.some(input =>
          union.members.has(input.value) && !input.disabled
        );
        union.toggle.closest('label')?.classList.toggle('lkf__choice--unavailable', union.toggle.disabled);
        union.controls.forEach(input => {
          if (input.matches('[data-lkf-choice]')) {
            if (!input.checked) input.disabled = this.unionDisabled.get(input) || false;
            input.closest('label')?.classList.toggle('lkf__choice--unavailable', input.disabled && !input.checked);
          }
        });
      }
    }

    applyUnionSelection(toggle) {
      const union = this.unions.find(item => item.toggle === toggle);
      if (!union) return;
      const enable = toggle.checked;
      // Selecting the shortcut means Used OR With defects, not New OR Used OR With defects.
      // Other FILTER GROUPS (author, availability, price, etc.) are never changed.
      for (const input of union.controls) {
        const member = union.members.has(input.value);
        if (!enable && !member) continue;
        const select = enable && member;
        if (input.type === 'hidden') {
          input.disabled = !select;
        } else {
          input.checked = select;
          // Include zero-count members in the OR request as well; Shopify renders
          // active zero-count values removable on the next response.
          input.disabled = select ? false : (this.unionDisabled.get(input) || false);
        }
      }
    }

    updateClearVisibility() {
      const current = new URL(window.location.href);
      const applied = hasFilterEntries(current.searchParams.entries()) || Number(this.dataset.activeCount || 0) > 0;
      let draft;
      try {
        const managed = [...this.querySelectorAll('[data-lkf-managed]')].map(el => el.dataset.lkfManaged);
        const base = this.pendingClear ? clearFilterURL(current.href, this.pageParam).href : current.href;
        const proposed = buildFilterURL(base, this.form.action, [...new FormData(this.form).entries()], managed, this.pageParam);
        draft = hasFilterEntries(proposed.searchParams.entries());
      } catch (_) {
        draft = hasFilterEntries(new FormData(this.form).entries());
      }
      // Outside controls refer to the results already applied, not the drawer draft.
      this.querySelectorAll('.lkf__mobile-clear').forEach(wrapper => { wrapper.hidden = !applied; });
      this.querySelectorAll('[data-lkf-clear]').forEach(link => {
        const outside = Boolean(link.closest('.lkf__mobile-clear')) || link.dataset.lkfClearScope === 'applied';
        link.hidden = !(outside ? applied : draft);
      });
    }

    updateGroupCounts() {
      this.updateUnionControls();
      this.groups.forEach(group => {
        let count = group.querySelectorAll('[data-lkf-choice]:checked').length;
        if (group.querySelector('[data-lkf-price]')) {
          count = [...group.querySelectorAll('[data-lkf-bound]')].some(input => input.value.trim() !== '') ? 1 : 0;
        }
        const summary = group.querySelector(':scope > summary');
        if (!summary) return;
        let badge = summary.querySelector('.lkf__selected-count');
        if (count > 0 && !badge) {
          badge = document.createElement('span');
          badge.className = 'lkf__selected-count';
          summary.insertBefore(badge, summary.querySelector('.lkf__caret'));
        }
        if (badge) { badge.textContent = String(count); badge.hidden = count === 0; }
      });
      this.updateClearVisibility();
    }

    updatePriceValidity() {
      this.prices.forEach(group => {
        const min = group.querySelector('[data-lkf-bound=min]');
        const max = group.querySelector('[data-lkf-bound=max]');
        const ceiling = Number(group.dataset.maximum);
        for (const input of [min, max]) {
          input.setCustomValidity('');
          const amount = parsePrice(input.value);
          if (amount !== null && (!Number.isFinite(amount) || amount < 0 || amount > ceiling)) {
            input.setCustomValidity(group.dataset.numberError || 'Enter a valid price within the displayed range.');
          }
        }
        const a = parsePrice(min.value), b = parsePrice(max.value);
        if (Number.isFinite(a) && Number.isFinite(b) && a > b) min.setCustomValidity(group.dataset.rangeError || 'Minimum exceeds maximum.');
      });
    }

    syncPriceFromFields(group) {
      const min = group.querySelector('[data-lkf-bound=min]');
      const max = group.querySelector('[data-lkf-bound=max]');
      const ceiling = Number(group.dataset.maximum);
      const a = parsePrice(min.value), b = parsePrice(max.value);
      if ((a !== null && !Number.isFinite(a)) || (b !== null && !Number.isFinite(b))) return;
      const lower = clamp(a ?? 0, 0, ceiling), upper = clamp(b ?? ceiling, 0, ceiling);
      if (lower > upper) return;
      group.querySelector('[data-lkf-range=min]').value = String(lower);
      group.querySelector('[data-lkf-range=max]').value = String(upper);
      this.paintRange(group);
    }
    priceText(cents) {
      return this.moneyFormatter ? this.moneyFormatter.format(cents / 100) : `${toAmount(cents)} ${this.dataset.currency || ''}`;
    }
    paintRange(group) {
      const ceiling = Number(group.dataset.maximum);
      const min = group.querySelector('[data-lkf-range=min]');
      const max = group.querySelector('[data-lkf-range=max]');
      const lower = Number(min.value), upper = Number(max.value);
      const slider = group.querySelector('[data-lkf-slider]');
      slider.style.setProperty('--lkf-low', `${ceiling ? lower / ceiling * 100 : 0}%`);
      slider.style.setProperty('--lkf-high', `${ceiling ? upper / ceiling * 100 : 0}%`);
      slider.dataset.atMaximum = String(lower === ceiling && ceiling > 0);
      min.setAttribute('aria-valuemax', String(upper));
      max.setAttribute('aria-valuemin', String(lower));
      min.setAttribute('aria-valuetext', this.priceText(lower));
      max.setAttribute('aria-valuetext', this.priceText(upper));
      const output = group.querySelector('[data-lkf-price-output]');
      if (output) output.textContent = `${this.priceText(lower)} – ${this.priceText(upper)}`;
    }
    rangeInput(input, { snap = true } = {}) {
      const group = input.closest('[data-lkf-price]');
      const ceiling = Number(group.dataset.maximum);
      const minRange = group.querySelector('[data-lkf-range=min]');
      const maxRange = group.querySelector('[data-lkf-range=max]');
      const lower = input.dataset.lkfRange === 'max' ? Number(minRange.value) : 0;
      const upper = input.dataset.lkfRange === 'min' ? Number(maxRange.value) : ceiling;
      const next = snap ? sliderValue(Number(input.value), this.increment, ceiling, lower, upper) : clamp(Number(input.value), lower, upper);
      input.value = String(next);
      // Only modify the corresponding named bound, leaving the other bound unset when untouched.
      const field = group.querySelector(`[data-lkf-bound="${input.dataset.lkfRange}"]`);
      field.value = (input.dataset.lkfRange === 'min' && next === 0) || (input.dataset.lkfRange === 'max' && next === ceiling) ? '' : toAmount(next);
      this.paintRange(group);
      this.updatePriceValidity();
      this.updateGroupCounts();
    }
    rangeKeydown(event) {
      const direction = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1, PageUp: 10, PageDown: -10 };
      if (!(event.key in direction) && !['Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const input = event.target;
      const group = input.closest('[data-lkf-price]');
      const lower = input.dataset.lkfRange === 'max' ? Number(group.querySelector('[data-lkf-range=min]').value) : 0;
      const upper = input.dataset.lkfRange === 'min' ? Number(group.querySelector('[data-lkf-range=max]').value) : Number(group.dataset.maximum);
      const value = event.key === 'Home' ? lower : event.key === 'End' ? upper : Number(input.value) + direction[event.key] * this.increment;
      input.value = String(clamp(value, lower, upper));
      this.rangeInput(input, { snap: false });
    }
    bindSliderTrack(group, options) {
      const slider = group.querySelector('[data-lkf-slider]');
      const fromPointer = event => {
        const rect = slider.getBoundingClientRect();
        const padding = parseFloat(getComputedStyle(document.documentElement).fontSize) * 1.2;
        const maximum = Number(group.dataset.maximum);
        return clamp((event.clientX - rect.left - padding) / Math.max(1, rect.width - 2 * padding), 0, 1) * maximum;
      };
      let dragged = null;
      slider.addEventListener('pointerdown', event => {
        if ((event.pointerType === 'mouse' && event.button !== 0) || event.target.matches('[data-lkf-range]')) return;
        if (Number(group.dataset.maximum) <= 0) return;
        const min = group.querySelector('[data-lkf-range=min]'), max = group.querySelector('[data-lkf-range=max]');
        const raw = fromPointer(event);
        dragged = Math.abs(raw - Number(min.value)) < Math.abs(raw - Number(max.value)) ? min : max;
        dragged.value = String(Math.round(raw));
        this.rangeInput(dragged);
        dragged.focus({ preventScroll: true });
        slider.setPointerCapture(event.pointerId);
        event.preventDefault();
      }, options);
      slider.addEventListener('pointermove', event => {
        if (!dragged || !slider.hasPointerCapture(event.pointerId)) return;
        dragged.value = String(Math.round(fromPointer(event)));
        this.rangeInput(dragged);
      }, options);
      for (const name of ['pointerup', 'pointercancel', 'lostpointercapture']) {
        slider.addEventListener(name, () => { dragged = null; }, options);
      }
    }

    searchValues(input) {
      const normalize = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
      const query = normalize(input.value.trim());
      const group = input.closest('[data-lkf-group]');
      let count = 0;
      group.querySelectorAll('[data-lkf-value-label]').forEach(item => {
        const matches = normalize(item.dataset.lkfValueLabel).includes(query);
        item.hidden = !matches;
        if (matches) count += 1;
      });
      group.querySelectorAll('[data-lkf-value-divider]').forEach(item => { item.hidden = query !== ''; });
      const empty = group.querySelector('[data-lkf-no-values]');
      if (empty) empty.hidden = count > 0;
    }

    clearDraft() {
      this.pendingClear = true;
      this.form.querySelectorAll('[data-lkf-choice]').forEach(input => { input.checked = false; });
      this.form.querySelectorAll('input[type=hidden]').forEach(input => { if (input.name.startsWith('filter.')) input.disabled = true; });
      this.form.querySelectorAll('[data-lkf-bound]').forEach(input => { input.value = ''; });
      this.querySelectorAll('[data-lkf-value-search]').forEach(input => { input.value = ''; this.searchValues(input); });
      this.updatePriceValidity();
      this.prices.forEach(group => this.syncPriceFromFields(group));
      this.updateGroupCounts();
      // Keep the sort selection, matching the semantics of clearing filters only.
    }

    submitFilters() {
      this.updatePriceValidity();
      const invalid = [...this.form.querySelectorAll('input, select')].find(input => !input.disabled && !input.validity.valid);
      if (invalid) {
        const group = invalid.closest('[data-lkf-group]');
        if (this.inMobile && !this.isOpen) this.openDrawer();
        if (group) group.open = true;
        invalid.focus();
        invalid.reportValidity();
        return;
      }
      const priceNames = new Map([...this.form.querySelectorAll('[data-lkf-bound]')].map(input => [input.name, input]));
      const entries = [...new FormData(this.form).entries()].map(([name, value]) => {
        if (!priceNames.has(name)) return [name, value];
        const amount = parsePrice(value);
        return [name, amount === null ? '' : toAmount(amount)];
      });
      const managed = [...this.querySelectorAll('[data-lkf-managed]')].map(el => el.dataset.lkfManaged);
      const base = this.pendingClear ? clearFilterURL(window.location.href, this.pageParam).toString() : window.location.href;
      const url = buildFilterURL(base, this.form.action, entries, managed, this.pageParam);
      if (this.isOpen) this.closeDrawer({ discard: false, restoreFocus: false });
      this.navigate(url);
    }

    onLinkClick(event) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const clear = event.target.closest('[data-lkf-clear]');
      if (clear && this.contains(clear)) {
        event.preventDefault();
        if (this.inMobile && this.isOpen) {
          this.clearDraft();
          // The Clear link may now be hidden. Keep keyboard focus inside the drawer.
          this.form.querySelector('.lkf__apply')?.focus({ preventScroll: true });
        } else this.navigate(clearFilterURL(window.location.href, this.pageParam));
        return;
      }
      const link = event.target.closest('[data-lkf-native-link]');
      if (!link || !this.contains(link)) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      const current = new URL(window.location.href);
      for (const key of new Set(current.searchParams.keys())) {
        if (key.startsWith('filter.') || ['page', this.pageParam, 'section_id', 'sections'].includes(key)) continue;
        if (!url.searchParams.has(key)) {
          for (const value of current.searchParams.getAll(key)) url.searchParams.append(key, value);
        }
      }
      url.searchParams.delete('page');
      url.searchParams.delete(this.pageParam);
      event.preventDefault();
      this.navigate(url);
    }

    saveState() {
      const groups = {};
      this.groups.forEach(group => { groups[group.dataset.lkfGroup] = group.open; });
      try { sessionStorage.setItem(this.stateKey, JSON.stringify({ groups })); } catch (_) { /* Optional preference. */ }
    }
    restoreGroupState() {
      try {
        const state = JSON.parse(sessionStorage.getItem(this.stateKey) || 'null');
        this.groups.forEach(group => {
          const saved = state?.groups?.[group.dataset.lkfGroup];
          if (typeof saved === 'boolean') group.open = saved;
          if (group.classList.contains('lkf__shortcut-group')) group.open = true;
        });
      } catch (_) { /* Stale/blocked storage must not break filtering. */ }
    }
    navigate(url) {
      this.saveState();
      // Used by tests and optional host integrations; ordinary storefront use navigates.
      const event = new CustomEvent('lokator:filters:navigate', { bubbles: true, cancelable: true, detail: { url: url.toString() } });
      if (this.dispatchEvent(event)) window.location.assign(url.toString());
    }
  }
  window.customElements.define('lokator-facets', LokatorFacets);
})();
